// LivretExplication.jsx
import { useState } from "react";

const GLOBAL_STYLE = {
  font: {
    family: "'Syne', system-ui, -apple-system, sans-serif",
    serif: "'DM Serif Display', Georgia, serif",           // fallback Georgia
    mono: "'JetBrains Mono', monospace",
  },
};

const SECTION_STYLE = {
  padding: "24px 28px",
  overflowY: "auto",
  flex: 1,
  background: "rgba(6, 9, 15, 0.6)",
};

const TAB_STYLE = {
  display: "flex",
  gap: 2,
  borderBottom: "1px solid rgba(255,255,255,0.08)",
  padding: "0 8px",
  flexWrap: "wrap",
};

const TAB_BUTTON_STYLE = (active, color) => ({
  padding: "8px 14px",
  fontSize: 12,
  fontWeight: 700,
  background: active ? `rgba(255,255,255,0.05)` : "transparent",
  border: active ? `1px solid ${color}40` : "1px solid transparent",
  borderBottom: active ? "none" : "1px solid transparent",
  color: active ? color : "rgba(255,255,255,0.4)",
  cursor: "pointer",
  transition: "all 0.2s",
  borderRadius: "8px 8px 0 0",
  letterSpacing: "0.05em",
});

const CARD_STYLE = (color = "#4ade80") => ({
  background: `${color}08`,
  border: `1px solid ${color}20`,
  borderRadius: 12,
  padding: "12px 16px",
  marginBottom: 16,
});

const TITLE_STYLE = {
  fontSize: 21,
  fontWeight: 700,                    // 800 → 700 (plus net)
  letterSpacing: "-0.02em",
  marginBottom: 10,
  color: "#fff",                      // retire le gradient qui floute
  textShadow: "0 2px 8px rgba(0,0,0,0.6)",   // ← aide beaucoup
};

const SUBTITLE_STYLE = {
  fontSize: 13.5,
  fontWeight: 700,
  color: "#4ade80",
  marginBottom: 6,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
};

const TEXT_STYLE = {
  fontSize: 14,                       // un peu plus gros
  lineHeight: 1.75,
  color: "rgba(255,255,255,0.78)",    // contraste augmenté
  fontFamily: GLOBAL_STYLE.font.serif,
};

const BADGE_STYLE = (color) => ({
  display: "inline-block",
  background: `${color}15`,
  border: `1px solid ${color}30`,
  borderRadius: 20,
  padding: "2px 8px",
  fontSize: 11,
  fontWeight: 700,
  color: color,
  marginRight: 6,
  marginBottom: 6,
});

const Grid2Col = ({ children }) => (
  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
    {children}
  </div>
);

const PhaseCard = ({ phase }) => (
  <div style={{ ...CARD_STYLE(phase.color), padding: "10px 12px" }}>
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
      <span style={{ fontSize: 24 }}>{phase.icon}</span>
      <div>
        <div style={{ fontSize: 12, fontWeight: 800, color: phase.color }}>{phase.name}</div>
        <div style={{ fontSize: 11, color: "rgba(255,255,255,0.3)" }}>{phase.boss}</div>
      </div>
    </div>
    <div style={{ fontSize: 12, color: "rgba(255,255,255,0.5)", marginBottom: 4 }}>⚡ {phase.rule}</div>
    <div style={{ fontSize: 11, color: "rgba(255,255,255,0.3)" }}>{phase.desc}</div>
  </div>
);

export default function LivretExplication({ onClose }) {
  const [activeTab, setActiveTab] = useState("intro");

  const tabs = [
    { id: "intro", label: "📖 Présentation", color: "#4ade80" },
    { id: "meca", label: "⚙️ Mécaniques", color: "#22d3ee" },
    { id: "heritage", label: "🔗 Héritages", color: "#a78bfa" },
    { id: "mondes", label: "🌑 Mondes parallèles", color: "#f87171" },
    { id: "phases", label: "🏛️ Les 7 phases", color: "#fbbf24" },
    { id: "conseils", label: "💡 Conseils", color: "#60a5fa" },
    { id: "glossaire", label: "📚 Glossaire", color: "#94a3b8" },
  ];

  const phasesData = [
    { icon: "🐻", name: "Préhistoire · Ours", boss: "L'Isolement", rule: "Cartes gratuites (pas de mana)", color: "#c97c3a", desc: "Le partage ou la domination : votre premier choix économique." },
    { icon: "🐝", name: "Antiquité · Abeille", boss: "L'Orfèvre", rule: "Monnaie double le mana suivant", color: "#f0a030", desc: "La valeur abstraite et la première dette." },
    { icon: "🦉", name: "Moyen Âge · Hibou", boss: "Le Seigneur féodal", rule: "Dîme : -1 mana sans carte production", color: "#8b6940", desc: "La rente foncière et la résistance des guildes." },
    { icon: "🐺", name: "Temps modernes · Loup", boss: "L'Usurier", rule: "Cartes sombres = -2 PV/tour", color: "#6060a0", desc: "Le crédit devient piège, naissance du syndicalisme." },
    { icon: "🐿️", name: "Contemporain · Écureuil", boss: "L'Inégalité systémique", rule: "Boss +1 ATK par tour", color: "#20a0a0", desc: "L'algorithme discrimine à grande échelle." },
    { icon: "🦊", name: "Mondialisation · Renard", boss: "Le Milliardaire", rule: "Immunité sans justice/régulation", color: "#e06020", desc: "La concentration extrême du capital." },
    { icon: "🦢", name: "Futur proche · Cigogne", boss: "L'Effondrement", rule: "Tous les combos -1 mana", color: "#40c0ff", desc: "Boucler l'odyssée par la coopération." },
  ];

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 2000,
        background: "rgba(3,5,12,0.96)",
        
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "24px",
        fontFamily: GLOBAL_STYLE.fontFamily,
      }}
      onClick={onClose}
    >
      <div
        style={{
          maxWidth: 900,
          width: "100%",
          height: "min(85vh, 700px)",
          background: "linear-gradient(145deg, #060d16, #03070d)",
          borderRadius: 20,
          border: "1px solid rgba(255,255,255,0.08)",
          boxShadow: "0 32px 64px rgba(0,0,0,0.8)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          animation: "fadeUp 0.3s ease-out",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* En-tête */}
        <div
          style={{
            padding: "12px 20px",
            borderBottom: "1px solid rgba(255,255,255,0.06)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexShrink: 0,
          }}
        >
          <div>
            <div style={{ fontSize: 11, letterSpacing: "0.3em", color: "#4ade80", fontWeight: 700 }}>
              PESACORE Family
            </div>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#fff" }}>L'Odyssée économique – Livret explicatif</div>
          </div>
          <button
            onClick={onClose}
            style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              background: "rgba(255,255,255,0.04)",
              border: "1px solid rgba(255,255,255,0.1)",
              color: "#fff",
              cursor: "pointer",
              fontSize: 16,
            }}
          >
            ✕
          </button>
        </div>

        {/* Onglets */}
        <div style={TAB_STYLE}>
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={TAB_BUTTON_STYLE(activeTab === tab.id, tab.color)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Contenu */}
        <div style={SECTION_STYLE}>
          {/* Présentation */}
          {activeTab === "intro" && (
            <div>
              <div style={TITLE_STYLE}>Bienvenue dans PESACORE Family</div>
              <div style={TEXT_STYLE}>
                PESACORE Family n’est pas un jeu de stratégie classique. C’est une <strong>expérience pédagogique</strong> qui
                vous fait revivre les grands tournants de l’économie à travers <strong>7 phases historiques</strong>.
                <br />
                <br />
                Vous incarnez une famille‑clan et affrontez un <strong>boss</strong> symbolisant un dysfonctionnement
                économique. Vos choix dans une phase créent des <strong>héritages</strong> (positifs ou négatifs) qui
                affectent les phases suivantes. Rien ne s’oublie.
              </div>
              <div style={{ ...CARD_STYLE("#4ade80"), marginTop: 20 }}>
                <div style={SUBTITLE_STYLE}>🎯 Objectif principal</div>
                <div style={TEXT_STYLE}>
                  Vaincre chaque boss en utilisant des cartes qui représentent des comportements économiques (confiance,
                  solidarité, mémoire, innovation…). Comprendre que l’économie est un système de choix hérités.
                </div>
              </div>
              <div style={CARD_STYLE("#fbbf24")}>
                <div style={SUBTITLE_STYLE}>⌛ Durée</div>
                <div style={TEXT_STYLE}>45 à 90 minutes – accessible dès 15 ans.</div>
              </div>
            </div>
          )}

          {/* Mécaniques */}
          {activeTab === "meca" && (
            <div>
              <div style={TITLE_STYLE}>⚙️ Mécaniques de base</div>
              <Grid2Col>
                <div style={CARD_STYLE("#22d3ee")}>
                  <div style={SUBTITLE_STYLE}>🃏 La carte</div>
                  <div style={TEXT_STYLE}>
                    ATK (dégâts au boss), DEF (réduction des attaques), coût en mana, comportement, effet spécial, et une{" "}
                    <strong>leçon économique</strong> réelle.
                  </div>
                </div>
                <div style={CARD_STYLE("#22d3ee")}>
                  <div style={SUBTITLE_STYLE}>✨ Mana</div>
                  <div style={TEXT_STYLE}>
                    Ressource pour jouer des cartes. Remise à son maximum chaque tour. Certaines cartes ou héritages
                    augmentent votre mana.
                  </div>
                </div>
                <div style={CARD_STYLE("#22d3ee")}>
                  <div style={SUBTITLE_STYLE}>🛡️ Champ & défense</div>
                  <div style={TEXT_STYLE}>
                    Les cartes posées restent sur le champ. En fin de tour, le boss attaque : ses dégâts sont réduits par
                    la moitié de votre DEF totale.
                  </div>
                </div>
                <div style={CARD_STYLE("#22d3ee")}>
                  <div style={SUBTITLE_STYLE}>🌀 Combos</div>
                  <div style={TEXT_STYLE}>
                    Associez 2 ou 3 comportements compatibles sur votre champ pour déclencher un bonus (ex. confiance +
                    solidarité = Réciprocité : +4 ATK pour tous). Les combos UNISON (3 cartes) sont très puissants.
                  </div>
                </div>
              </Grid2Col>
              <div style={CARD_STYLE("#f87171")}>
                <div style={SUBTITLE_STYLE}>☠️ Cartes sombres</div>
                <div style={TEXT_STYLE}>
                  Les comportements comme domination, cupidité, inégalité augmentent votre{" "}
                  <strong>corruption</strong>. À 3 points, un Monde parallèle s’ouvre.
                </div>
              </div>
            </div>
          )}

          {/* Héritages */}
          {activeTab === "heritage" && (
            <div>
              <div style={TITLE_STYLE}>🔗 L’effet domino – Les héritages</div>
              <div style={TEXT_STYLE}>
                À la fin de chaque phase, votre bilan (combos, cartes sombres/lumineuses, etc.) génère des{" "}
                <strong>héritages</strong> pour la phase suivante. Ils peuvent être :
              </div>
              <div style={{ display: "flex", gap: 12, marginTop: 16, flexWrap: "wrap" }}>
                <div style={{ ...BADGE_STYLE("#4ade80"), fontSize: 12, padding: "4px 12px" }}>✦ Bonus</div>
                <div style={{ ...BADGE_STYLE("#f87171"), fontSize: 12, padding: "4px 12px" }}>⚠ Malus</div>
                <div style={{ ...BADGE_STYLE("#dc2626"), fontSize: 12, padding: "4px 12px" }}>☠ Systémique</div>
              </div>
              <div style={CARD_STYLE("#a78bfa")}>
                <div style={SUBTITLE_STYLE}>Exemples</div>
                <div style={TEXT_STYLE}>
                  • <strong>Réseau de confiance</strong> (bonus) : +1 Mana, cartes 'échange' moins chères.<br />
                  • <strong>Servitude normalisée</strong> (malus) : cartes 'travail' -2 ATK.<br />
                  • <strong>Violence fondatrice</strong> (systémique) : boss +6 HP, carte imposée.
                </div>
              </div>
              <div style={TEXT_STYLE}>
                Les héritages sont <strong>irréversibles</strong> et s’accumulent. Ils apparaissent dans l’interface à
                droite pendant les phases.
              </div>
            </div>
          )}

          {/* Mondes parallèles */}
          {activeTab === "mondes" && (
            <div>
              <div style={TITLE_STYLE}>🌑 Mondes parallèles</div>
              <div style={TEXT_STYLE}>
                Si votre corruption atteint <strong>3</strong>, vous basculez temporairement dans un Monde parallèle lié au
                thème de la phase. Une <strong>énigme économique</strong> vous est posée.
              </div>
              <div style={CARD_STYLE("#f87171")}>
                <div style={SUBTITLE_STYLE}>🔍 Principe</div>
                <div style={TEXT_STYLE}>
                  Une situation narrative est décrite, puis une question avec plusieurs réponses. La bonne réponse
                  débloque une <strong>carte parallèle</strong> (rare, puissante, avec une leçon unique).
                  <br />
                  <em>Ce n’est pas punitif – c’est pédagogique. Vous pouvez fermer le monde parallèle sans répondre.</em>
                </div>
              </div>
              <div style={CARD_STYLE("#a78bfa")}>
                <div style={SUBTITLE_STYLE}>🧠 Exemple (Phase 1 – Clan dominant)</div>
                <div style={TEXT_STYLE}>
                  <em>« Le clan Ours a tout le gibier. Un vieux membre propose quoi pour sortir de la dépendance ? »</em>
                  <br />
                  Bonne réponse : « Créer un surplus partagé entre plusieurs clans. »
                </div>
              </div>
            </div>
          )}

          {/* Les 7 phases */}
          {activeTab === "phases" && (
            <div>
              <div style={TITLE_STYLE}>🏛️ Les 7 phases de l’Odyssée</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 8 }}>
                {phasesData.map((p) => (
                  <PhaseCard key={p.name} phase={p} />
                ))}
              </div>
            </div>
          )}

          {/* Conseils */}
          {activeTab === "conseils" && (
            <div>
              <div style={TITLE_STYLE}>💡 Conseils stratégiques</div>
              <ul style={{ ...TEXT_STYLE, paddingLeft: 20 }}>
                <li>🐻 <strong>Ne négligez pas la défense</strong> – Sans DEF, vous perdez 4 à 6 PV par tour.</li>
                <li>⚖️ <strong>Anticipez l’héritage</strong> – Trop de cartes sombres = malus lourds plus tard.</li>
                <li>🌀 <strong>Exploitez les combos</strong> – Un combo peut doubler votre puissance. Gardez des cartes complémentaires en main.</li>
                <li>📚 <strong>Lisez les leçons</strong> – Elles contiennent les clés des énigmes des mondes parallèles.</li>
                <li>❓ <strong>Utilisez le Guide de phase</strong> – Chaque phase a un guide expliquant les dangers (Dîme, Algorithme, Immunité…).</li>
                <li>🎚️ <strong>Gérez votre mana</strong> – Les cartes « production » ou « monnaie » peuvent vous donner du mana supplémentaire.</li>
              </ul>
              <div style={CARD_STYLE("#60a5fa")}>
                <div style={SUBTITLE_STYLE}>⏸️ Pause et codex</div>
                <div style={TEXT_STYLE}>
                  Appuyez sur <strong>Échap</strong> ou le bouton ⏸ pour suspendre la partie. Le <strong>Codex</strong> (📖)
                  rassemble tous les pivots historiques, leçons, règles et combos débloqués.
                </div>
              </div>
            </div>
          )}

          {/* Glossaire */}
          {activeTab === "glossaire" && (
            <div>
              <div style={TITLE_STYLE}>📚 Glossaire rapide</div>
              <Grid2Col>
                <div style={CARD_STYLE("#94a3b8")}>
                  <span style={{ fontWeight: 800 }}>Corruption</span>
                  <div style={TEXT_STYLE}>Compteur qui monte en jouant des cartes sombres. À 3 → Monde parallèle.</div>
                </div>
                <div style={CARD_STYLE("#94a3b8")}>
                  <span style={{ fontWeight: 800 }}>Éthique</span>
                  <div style={TEXT_STYLE}>Inverse de la corruption ; certaines cartes en dépendent.</div>
                </div>
                <div style={CARD_STYLE("#94a3b8")}>
                  <span style={{ fontWeight: 800 }}>Carte imposée</span>
                  <div style={TEXT_STYLE}>Carte ajoutée de force dans votre main par un héritage ou une stratégie. Coût 0 mana.</div>
                </div>
                <div style={CARD_STYLE("#94a3b8")}>
                  <span style={{ fontWeight: 800 }}>Unison</span>
                  <div style={TEXT_STYLE}>Combo à trois cartes (ex. Clan Solidaire, Essor, Tyrannie). Effet puissant + cinématique.</div>
                </div>
                <div style={CARD_STYLE("#94a3b8")}>
                  <span style={{ fontWeight: 800 }}>Pivot</span>
                  <div style={TEXT_STYLE}>Événement historique réel affiché entre deux phases (ex. Bretton Woods, Grande Dérégulation).</div>
                </div>
                <div style={CARD_STYLE("#94a3b8")}>
                  <span style={{ fontWeight: 800 }}>Codex</span>
                  <div style={TEXT_STYLE}>Encyclopédie intégrée qui recense vos découvertes.</div>
                </div>
              </Grid2Col>
            </div>
          )}
        </div>

        {/* Note de bas */}
        <div
          style={{
            padding: "10px 20px",
            borderTop: "1px solid rgba(255,255,255,0.04)",
            fontSize: 10,
            color: "rgba(255,255,255,0.2)",
            textAlign: "center",
            flexShrink: 0,
          }}
        >
          PESACORE Family – L'Odyssée économique – Chaque mécanique existe dans l'économie réelle.
        </div>
      </div>
    </div>
  );
}